import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CollaborationTools = ({ collaborators, onCollaboratorAction }) => {
  const [activeTab, setActiveTab] = useState('team'); // team, comments, versions, activity
  const [newComment, setNewComment] = useState('');
  const [selectedElement, setSelectedElement] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');

  // Mock collaboration data
  const collaborationData = {
    team: [
    {
      id: 1,
      name: 'Sarah Chen',
      email: 'sarah@pengamepr.com',
      role: 'Editor',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786',
      status: 'online',
      lastActive: 'Now',
      permissions: ['edit', 'comment', 'approve']
    },
    {
      id: 2,
      name: 'Marcus Johnson',
      email: 'marcus@stellarpublishing.com',
      role: 'Publisher',
      avatar: "https://images.unsplash.com/photo-1622127921821-48c41c3eb98b",
      status: 'away',
      lastActive: '2 hours ago',
      permissions: ['view', 'comment', 'approve']
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      email: 'emma@designstudio.com',
      role: 'Designer',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
      status: 'offline',
      lastActive: '1 day ago',
      permissions: ['edit', 'comment']
    }],

    comments: [
    {
      id: 1,
      elementId: 'author-bio-1',
      user: { name: 'Sarah Chen', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786' },
      comment: 'The author bio could use more specific achievements. Maybe mention the awards?',
      timestamp: '2 hours ago',
      resolved: false,
      replies: [
      {
        id: 11,
        user: { name: 'Marcus Johnson', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e' },
        comment: 'Agreed. The Nebula Award nomination should definitely be highlighted.',
        timestamp: '1 hour ago'
      }]

    },
    {
      id: 2,
      elementId: 'press-release-1',
      user: { name: 'Emma Rodriguez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80' },
      comment: 'The headline could be more compelling. What about emphasizing the quantum physics angle?',
      timestamp: '4 hours ago',
      resolved: true
    }],

    versions: [
    {
      id: 'v1.2',
      name: 'Final Review Version',
      author: 'Sarah Chen',
      timestamp: '2 hours ago',
      changes: ['Updated author bio', 'Enhanced press release', 'Added media gallery'],
      status: 'current'
    },
    {
      id: 'v1.1',
      name: 'Publisher Feedback',
      author: 'Marcus Johnson',
      timestamp: '1 day ago',
      changes: ['Revised book description', 'Updated publication date'],
      status: 'archived'
    },
    {
      id: 'v1.0',
      name: 'Initial Draft',
      author: 'Emma Rodriguez',
      timestamp: '3 days ago',
      changes: ['Created initial layout', 'Added basic elements'],
      status: 'archived'
    }],

    activity: [
    {
      id: 1,
      user: 'Sarah Chen',
      action: 'approved changes to author bio',
      timestamp: '30 minutes ago',
      type: 'approval'
    },
    {
      id: 2,
      user: 'Marcus Johnson',
      action: 'added comment on press release',
      timestamp: '2 hours ago',
      type: 'comment'
    },
    {
      id: 3,
      user: 'Emma Rodriguez',
      action: 'updated media gallery layout',
      timestamp: '4 hours ago',
      type: 'edit'
    },
    {
      id: 4,
      user: 'Sarah Chen',
      action: 'invited new team member',
      timestamp: '1 day ago',
      type: 'invite'
    }]

  };

  const handleAddComment = () => {
    if (!newComment?.trim()) return;

    onCollaboratorAction?.('add-comment', {
      elementId: selectedElement,
      comment: newComment,
      user: 'Current User'
    });

    setNewComment('');
  };

  const handleInviteTeamMember = () => {
    if (!inviteEmail?.trim()) return;

    // Handle team invitation logic
    console.log('Inviting:', inviteEmail);
    setInviteEmail('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':return 'bg-green-400';
      case 'away':return 'bg-yellow-400';
      default:return 'bg-slate-300';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'approval':return 'CheckCircle';
      case 'comment':return 'MessageCircle';
      case 'edit':return 'Edit2';
      case 'invite':return 'UserPlus';
      default:return 'Activity';
    }
  };

  const collaborationTabs = [
  { id: 'team', label: 'Team', icon: 'Users' },
  { id: 'comments', label: 'Comments', icon: 'MessageCircle' },
  { id: 'versions', label: 'Versions', icon: 'GitBranch' },
  { id: 'activity', label: 'Activity', icon: 'Activity' }];


  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
      {/* Collaboration Header */}
      <div className="p-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">Collaboration</h3>
        
        {/* Collaboration Tabs */}
        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {collaborationTabs?.map((tab) =>
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
            activeTab === tab?.id ?
            'bg-white text-slate-900 shadow-sm' :
            'text-slate-600 hover:text-slate-900'}`
            }>

              <Icon name={tab?.icon} size={12} />
              <span>{tab?.label}</span>
            </button>
          )}
        </div>
      </div>
      {/* Team Management */}
      {activeTab === 'team' &&
      <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-slate-200">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Team Members</h4>
            <div className="space-y-3">
              {collaborationData?.team?.map((member) =>
            <div key={member?.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded-lg">
                  <div className="relative">
                    <img
                  src={member?.avatar}
                  alt={`Profile photo of ${member?.name}, team member with ${member?.role} role`}
                  className="w-8 h-8 rounded-full object-cover" />

                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${getStatusColor(member?.status)} border-2 border-white rounded-full`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-900 truncate">{member?.name}</p>
                      <span className="text-xs text-slate-500">{member?.lastActive}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-xs text-slate-500">{member?.role}</p>
                      <div className="flex space-x-1">
                        {member?.permissions?.map((permission, index) =>
                    <span
                      key={index}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">

                            {permission}
                          </span>
                    )}
                      </div>
                    </div>
                  </div>
                </div>
            )}
            </div>
          </div>

          {/* Invite New Member */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Invite Team Member</h4>
            <div className="space-y-3">
              <input
              type="email"
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />

              <select className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
              <button
              onClick={handleInviteTeamMember}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm">

                <Icon name="Send" size={14} />
                <span>Send Invitation</span>
              </button>
            </div>
          </div>
        </div>
      }
      {/* Comments */}
      {activeTab === 'comments' &&
      <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {collaborationData?.comments?.map((comment) =>
          <div key={comment?.id} className="space-y-2">
                <div className="flex items-start space-x-3">
                  <img
                src={comment?.user?.avatar}
                alt={`Avatar of ${comment?.user?.name} who left a comment on the press kit element`}
                className="w-6 h-6 rounded-full object-cover" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-900">{comment?.user?.name}</span>
                      <span className="text-xs text-slate-500">{comment?.timestamp}</span>
                      {comment?.resolved &&
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Resolved
                        </span>
                  }
                    </div>
                    <p className="text-sm text-slate-700 mt-1">{comment?.comment}</p>
                    
                    {/* Comment Replies */}
                    {comment?.replies &&
                <div className="mt-2 ml-4 space-y-2">
                        {comment?.replies?.map((reply) =>
                  <div key={reply?.id} className="flex items-start space-x-2">
                            <img
                      src={reply?.user?.avatar}
                      alt={`Avatar of ${reply?.user?.name} who replied to comment`}
                      className="w-5 h-5 rounded-full object-cover" />

                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-slate-700">{reply?.user?.name}</span>
                                <span className="text-xs text-slate-500">{reply?.timestamp}</span>
                              </div>
                              <p className="text-xs text-slate-600 mt-1">{reply?.comment}</p>
                            </div>
                          </div>
                  )}
                      </div>
                }
                    
                    {/* Comment Actions */}
                    <div className="flex items-center space-x-3 mt-2">
                      <button className="text-xs text-slate-500 hover:text-slate-700">Reply</button>
                      {!comment?.resolved &&
                  <button className="text-xs text-green-600 hover:text-green-700">Resolve</button>
                  }
                    </div>
                  </div>
                </div>
              </div>
          )}
          </div>

          {/* Add Comment */}
          <div className="p-4 border-t border-slate-200 space-y-3">
            <select
            value={selectedElement || ''}
            onChange={(e) => setSelectedElement(e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">

              <option value="">Select element to comment on</option>
              <option value="author-bio-1">Author Biography</option>
              <option value="press-release-1">Press Release</option>
              <option value="media-gallery-1">Media Gallery</option>
              <option value="book-cover-1">Book Cover</option>
            </select>
            <textarea
            placeholder="Add your comment..."
            value={newComment}
            onChange={(e) => setNewComment(e?.target?.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />

            <button
            onClick={handleAddComment}
            disabled={!newComment?.trim() || !selectedElement}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed">

              <Icon name="MessageCircle" size={14} />
              <span>Add Comment</span>
            </button>
          </div>
        </div>
      }
      {/* Version History */}
      {activeTab === 'versions' &&
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {collaborationData?.versions?.map((version) =>
        <div
          key={version?.id}
          className={`p-3 border rounded-lg transition-colors ${
          version?.status === 'current' ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`
          }>

              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-slate-900">{version?.id}</span>
                  {version?.status === 'current' &&
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      Current
                    </span>
              }
                </div>
                <span className="text-xs text-slate-500">{version?.timestamp}</span>
              </div>
              <h5 className="text-sm font-medium text-slate-700 mb-1">{version?.name}</h5>
              <p className="text-xs text-slate-500 mb-2">by {version?.author}</p>
              <div className="space-y-1">
                {version?.changes?.map((change, index) =>
            <div key={index} className="flex items-center space-x-2">
                    <div className="w-1 h-1 bg-slate-400 rounded-full" />
                    <span className="text-xs text-slate-600">{change}</span>
                  </div>
            )}
              </div>
              {version?.status !== 'current' &&
          <div className="flex items-center space-x-2 mt-2">
                  <button className="text-xs text-indigo-600 hover:text-indigo-700">Restore</button>
                  <button className="text-xs text-slate-500 hover:text-slate-700">Compare</button>
                </div>
          }
            </div>
        )}
        </div>
      }
      {/* Activity Feed */}
      {activeTab === 'activity' &&
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {collaborationData?.activity?.map((activity) =>
        <div key={activity?.id} className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center">
                <Icon name={getActivityIcon(activity?.type)} size={12} className="text-slate-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700">
                  <span className="font-medium">{activity?.user}</span> {activity?.action}
                </p>
                <p className="text-xs text-slate-500">{activity?.timestamp}</p>
              </div>
            </div>
        )}
        </div>
      }
    </div>);

};

export default CollaborationTools;